import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import useSeo from '@/hooks/useSeo';
import { loreArticles, loreCategories } from '@/mocks/lore';
import { siteConfig } from '@/mocks/site';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '洞洞杂货铺漫画设定与世界观',
  description: '洞洞杂货铺漫画世界观、故事背景、洞洞含义等完整设定解析文章合集。',
};

export default function LoreList() {
  const [active, setActive] = useState('all');
  useSeo({
    title: '洞洞杂货铺设定与世界观 - 故事背景与洞洞含义解析大全',
    description: '汇集洞洞杂货铺漫画的世界观设定、故事背景、角色来历与洞洞含义等全部深度解读文章，帮你快速看懂这部漫画的独特设定逻辑。',
    keywords: '洞洞杂货铺设定,洞洞杂货铺世界观,洞洞杂货铺故事背景,洞洞杂货铺洞洞含义',
    ogType: 'website',
    jsonLd,
  });

  const filtered = useMemo(
    () => (active === 'all' ? loreArticles : loreArticles.filter((a) => a.category === active)),
    [active],
  );

  return (
    <Layout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-6xl px-4 py-14 md:px-6 md:py-16">
          <nav aria-label="面包屑" className="text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">首页</Link>
            <span className="mx-2 text-foreground-300">/</span>
            <span className="text-foreground-700">设定与世界观</span>
          </nav>
          <h1 className="mt-4 text-2xl leading-relaxed text-foreground-950 md:text-3xl">设定与世界观</h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-foreground-600">
            从《洞洞杂货铺》的完整设定、世界观与故事背景出发，为你系统梳理这部漫画的独特逻辑。点击标签可筛选不同维度的内容。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-12">
        <div className="flex flex-wrap gap-2">
          {loreCategories.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setActive(c.key)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                active === c.key
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="mt-9 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => (
            <Link
              key={a.slug}
              to={`/lore/${a.slug}`}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
            >
              <div className="h-52 overflow-hidden">
                <img
                  src={a.cover}
                  alt={a.coverAlt}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-accent-100 px-3 py-0.5 text-xs text-accent-800">{a.category}</span>
                  {a.tag && (
                    <span className="rounded-full bg-secondary-100 px-3 py-0.5 text-xs text-secondary-900">{a.tag}</span>
                  )}
                </div>
                <h2 className="mt-3 text-base font-semibold leading-snug text-foreground-950 group-hover:text-primary-700">
                  {a.title}
                </h2>
                <p className="mt-2 flex-1 line-clamp-3 text-sm leading-relaxed text-foreground-600">{a.excerpt}</p>
                <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
                  <span>{a.date}</span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-eye-line text-sm" aria-hidden="true"></i>
                    {a.views} 阅读
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}